import React, { useState } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';

const Portfolio = () => {
  const { isDark } = useDarkMode();
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Images matched to each project's concept; mix of Unsplash (topic-specific) and Picsum (reliable fallback)
  const portfolioItems = [
    {
      id: 1,
      title: 'Restaurant Campaign',
      category: 'F&B Branding',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=400&fit=crop',
      description: 'Increased engagement by 250% in 1 month through targeted content and influencer partnerships.',
    },
    {
      id: 2,
      title: 'Fashion Brand Launch',
      category: 'Fashion & Lifestyle',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop',
      description: 'Successful product launch reaching 500K+ impressions with high conversion rate.',
    },
    {
      id: 3,
      title: 'Tech Startup Growth',
      category: 'B2B Tech',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&h=400&fit=crop',
      description: 'Scaled startup presence from 10K to 100K followers in 6 months.',
    },
    {
      id: 4,
      title: 'Beauty Brand Campaign',
      category: 'Cosmetics',
      image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600&h=400&fit=crop',
      description: 'Influencer-driven campaign generating $150K in direct sales.',
    },
    {
      id: 5,
      title: 'Fitness Community',
      category: 'Health & Wellness',
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop',
      description: 'Built engaged community of 80K+ fitness enthusiasts and advocates.',
    },
    {
      id: 6,
      title: 'Real Estate Success',
      category: 'Real Estate',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop',
      description: 'Generated qualified leads resulting in $2M+ in property sales.',
    },
    {
      id: 7,
      title: 'E-Commerce Boost',
      category: 'Retail',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop',
      description: 'Increased online sales by 180% through strategic social commerce.',
    },
    {
      id: 8,
      title: 'Travel Influencer Network',
      category: 'Travel & Tourism',
      image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=400&fit=crop',
      description: 'Created viral travel content series reaching 2M+ views.',
    },
    {
      id: 9,
      title: 'Nusuk App Promotion',
      category: 'App Promotion',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=400&fit=crop',
      description: 'Successfully promoted Nusuk App through influencer marketing and strategic Instagram campaigns. Increased installs, engagement, and brand visibility using targeted content strategies.',
    },
  ];

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(portfolioItems[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % portfolioItems.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(portfolioItems[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentIndex - 1 + portfolioItems.length) % portfolioItems.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(portfolioItems[prevIndex]);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  return (
    <section
      id="portfolio"
      className={`relative w-full py-20 lg:py-32 transition-colors duration-500 ${
        isDark ? 'bg-dark-primary' : 'bg-light-secondary'
      }`}
    >
      {/* Background Decorations */}
      <motion.div
        className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl -z-10 ${
          isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/8'
        }`}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 7, repeat: Infinity }}
      ></motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          <motion.p
            className="text-primary-gold font-montserrat font-semibold uppercase tracking-widest text-sm mb-2"
            animate={{ letterSpacing: [0, '0.1em', 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            Success Stories
          </motion.p>
          <motion.h2
            className={`font-montserrat font-bold text-4xl lg:text-5xl mb-4 transition-colors ${
              isDark ? 'text-white' : 'text-black'
            }`}
            variants={itemVariants}
          >
            Our Work
          </motion.h2>
          <motion.p
            className={`font-poppins max-w-2xl mx-auto text-lg transition-colors ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
            variants={itemVariants}
          >
            Showcasing campaigns that drove real results for our clients
          </motion.p>
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.id}
              onClick={() => openLightbox(index)}
              className={`group relative h-80 rounded-xl overflow-hidden cursor-pointer border transition-all duration-300 ${
                isDark
                  ? 'bg-dark-secondary border-primary-gold/10 hover:border-primary-gold/40 hover:shadow-gold-glow'
                  : 'bg-light-tertiary border-primary-gold/20 hover:border-primary-gold/40 hover:shadow-lg'
              }`}
              variants={itemVariants}
              whileHover={{
                y: -8,
                scale: 1.02,
              }}
            >
              {/* Image */}
              <motion.div
                className="w-full h-full bg-gradient-to-br from-primary-gold/10 to-primary-gold/5 flex items-center justify-center overflow-hidden"
              >
                <motion.img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => { 
                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600"%3E%3Crect fill="%23daa520" width="800" height="600"/%3E%3Ctext x="50%25" y="50%25" font-size="48" fill="white" text-anchor="middle" dy=".3em" font-family="Arial"%3EProject Image%3C/text%3E%3C/svg%3E';
                    e.currentTarget.style.opacity = 1;
                  }}
                  onLoad={(e) => { e.currentTarget.style.opacity = 1; }}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  style={{ opacity: 0 }}
                  whileHover={{
                    scale: 1.15,
                  }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />
              </motion.div>

              {/* Overlay */}
              <motion.div
                className={`absolute inset-0 ${
                  isDark
                    ? 'bg-gradient-to-t from-black via-black/50 to-transparent'
                    : 'bg-gradient-to-t from-black/90 via-black/50 to-transparent'
                }`}
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              ></motion.div>

              {/* Content */}
              <motion.div
                className="absolute inset-0 flex flex-col justify-end p-6"
                initial={{ y: 20, opacity: 0 }}
                whileHover={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="space-y-2"
                  variants={containerVariants}
                >
                  <motion.p className="text-primary-gold font-montserrat font-semibold text-xs uppercase tracking-widest">
                    {item.category}
                  </motion.p>
                  <motion.h3 className="font-montserrat font-bold text-xl text-white">
                    {item.title}
                  </motion.h3>
                  <motion.p className="font-poppins text-sm text-gray-200 line-clamp-2">
                    {item.description}
                  </motion.p>
                </motion.div>
              </motion.div>

              {/* Hover Icon with glow */}
              <motion.div
                className={`absolute top-4 right-4 bg-primary-gold text-black p-2 rounded-full transition-all duration-300 ${
                  isDark ? 'shadow-gold-glow' : 'shadow-lg'
                }`}
                initial={{ opacity: 0, scale: 0, rotate: -90 }}
                whileHover={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.4 }}
                whileInView={{ opacity: 0 }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center"
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          <motion.button
            onClick={() => {
              const portfolioSection = document.getElementById('portfolio');
              portfolioSection?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`px-10 py-4 bg-primary-gold text-black font-montserrat font-bold rounded-lg transition-all duration-300 inline-block cursor-pointer ${
              isDark ? 'hover:shadow-gold-glow-lg' : 'hover:shadow-lg'
            }`}
            whileHover={{
              scale: 1.05,
              y: -4,
            }}
            whileTap={{ scale: 0.95 }}
          >
            View All Projects
          </motion.button>
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className={`fixed inset-0 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${
              isDark ? 'bg-black/95' : 'bg-black/90'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Close Button */}
            <motion.button
              onClick={closeLightbox}
              className={`absolute top-6 right-6 text-3xl transition-colors z-50 ${
                isDark ? 'text-primary-gold hover:text-white' : 'text-primary-gold hover:text-yellow-300'
              }`}
              whileHover={{ scale: 1.2, rotate: 90 }}
            >
              <FiX />
            </motion.button>

            {/* Content */}
            <motion.div
              className="max-w-4xl w-full relative"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              {/* Image */}
              <motion.div
                className="relative mb-6 rounded-xl overflow-hidden"
                layoutId="portfolioImage"
              >
                <img
                  src={selectedImage.image}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-96 object-cover rounded-xl"
                />
              </motion.div>

              {/* Details */}
              <motion.div
                className="space-y-4 px-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <p className="text-primary-gold font-montserrat font-semibold uppercase tracking-widest text-xs">
                  {selectedImage.category}
                </p>
                <h3 className="font-montserrat font-bold text-3xl text-white">
                  {selectedImage.title}
                </h3>
                <p className="font-poppins text-gray-300 text-lg leading-relaxed">
                  {selectedImage.description}
                </p>
              </motion.div>

              {/* Navigation */}
              <motion.div
                className="flex justify-between mt-8 px-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <motion.button
                  onClick={prevImage}
                  className={`p-3 rounded-full bg-primary-gold text-black transition-all ${
                    isDark ? 'hover:shadow-gold-glow-lg' : 'hover:shadow-lg'
                  }`}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiChevronLeft className="w-6 h-6" />
                </motion.button>
                <motion.button
                  onClick={nextImage}
                  className={`p-3 rounded-full bg-primary-gold text-black transition-all ${
                    isDark ? 'hover:shadow-gold-glow-lg' : 'hover:shadow-lg'
                  }`}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiChevronRight className="w-6 h-6" />
                </motion.button>
              </motion.div>

              {/* Counter */}
              <motion.div
                className={`text-center mt-6 font-poppins ${isDark ? 'text-gray-400' : 'text-gray-300'}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {currentIndex + 1} / {portfolioItems.length}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
